import {
  type GraphQLService,
  workspaceApiFetch,
} from '@affine/core/modules/cloud';
import type { DocsService } from '@affine/core/modules/doc';
import type { GuardService } from '@affine/core/modules/permissions';
import {
  type WorkspaceBlobContext,
  workspaceBlobUrl,
} from '@affine/core/modules/workspace/utils/blob-cache';
import { mailMessageBodyQuery } from '@affine/graphql';
import type { I18nInstance } from '@affine/i18n';
import type { SurfaceBlockModel } from '@blocksuite/affine/blocks/surface';
import { findFreeBlockSnapshotContentBound } from '@blocksuite/affine/gfx/template';
import { Bound } from '@blocksuite/affine/global/gfx';
import { type Store, Text } from '@blocksuite/affine/store';
import {
  isFileIndexEligibleDocId,
  reconcileFileIndexDb,
} from '@blocksuite/affine-block-database';
import { ConnectorMode, PointStyle } from '@blocksuite/affine-model';
import {
  EMBED_CARD_HEIGHT,
  EMBED_CARD_WIDTH,
} from '@blocksuite/affine-shared/consts';

import { uploadWorkspaceBlob } from '../workflow-viewer/widgets/upload-blob';
import type { MailHtmlImportDialogSource } from './mail-html-import-dialog';
import {
  buildMailHtmlSrcDoc,
  type MailRemoteImage,
  type SanitizedMailHtml,
  sanitizeMailHtml,
} from './mail-html-sanitizer';
import { mailText } from './mail-i18n';
import {
  formatMailBytes,
  formatMailDate,
  formatMailSenderDisplayName,
  type MailAttachmentInfo,
  type MailMessageBodyInfo,
  type MailMessageInfo,
} from './types';

export const MAIL_DROP_MAX_MESSAGE_COUNT = 10;
export const MAIL_MESSAGE_CLIPBOARD_TYPE =
  'application/x-streamlain-mail-message-ids';
export const MAIL_ATTACHMENT_CLIPBOARD_TYPE =
  'application/x-streamlain-mail-attachments';

const MAIL_HTML_EXPORT_MAX_BYTES = 5 * 1024 * 1024;
const MAIL_HTML_ATTACHMENT_STYLE = 'html' as const;
const MAIL_ORIGINAL_ATTACHMENT_STYLE = 'horizontalThin' as const;
const MAIL_HTML_A4_HEIGHT_TO_WIDTH_RATIO = 297 / 210;
const MAIL_HTML_PLACEMENT_GAP = 160;
const MAIL_ORIGINAL_ATTACHMENT_COLUMN_GAP = 64;
const MAIL_ORIGINAL_ATTACHMENT_STACK_GAP = 20;
const MAIL_HTML_EXPORT_BACKGROUND_COLOR = '#f6f8fa';
const MAIL_HTML_EXPORT_SURFACE_BACKGROUND_COLOR = '#ffffff';
const MAIL_HTML_EXPORT_PADDING_PX = 24;

export interface MailMessageClipboardPayload {
  kind: 'streamlain-mail-messages';
  workspaceId: string;
  messages: MailMessageInfo[];
}

export interface MailAttachmentClipboardPayload {
  kind: 'streamlain-mail-attachments';
  workspaceId: string;
  attachments: MailAttachmentInfo[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function parseJsonPayload<T>(value: string): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function uniqueById<T extends { id: string }>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    if (!item.id || seen.has(item.id)) continue;
    seen.add(item.id);
    result.push(item);
  }
  return result;
}

export function writeMailMessageClipboardPayload(
  dataTransfer: DataTransfer,
  payload: MailMessageClipboardPayload
) {
  const messages = uniqueById(payload.messages);
  const nextPayload: MailMessageClipboardPayload = {
    kind: 'streamlain-mail-messages',
    workspaceId: payload.workspaceId,
    messages,
  };
  dataTransfer.setData(
    MAIL_MESSAGE_CLIPBOARD_TYPE,
    JSON.stringify(nextPayload)
  );
  dataTransfer.setData(
    'text/plain',
    messages
      .map(message => message.subject?.trim() || message.snippet?.trim())
      .filter(Boolean)
      .join('\n')
  );
}

export function readMailMessageClipboardPayload(
  dataTransfer: DataTransfer | null
): MailMessageClipboardPayload | null {
  const raw = dataTransfer?.getData(MAIL_MESSAGE_CLIPBOARD_TYPE);
  const parsed = parseJsonPayload<unknown>(raw ?? '');
  if (
    !isRecord(parsed) ||
    parsed.kind !== 'streamlain-mail-messages' ||
    typeof parsed.workspaceId !== 'string' ||
    !Array.isArray(parsed.messages)
  ) {
    return null;
  }
  const messages = parsed.messages.filter(isRecord) as MailMessageInfo[];
  if (messages.length === 0) return null;
  return {
    kind: 'streamlain-mail-messages',
    workspaceId: parsed.workspaceId,
    messages: uniqueById(messages),
  };
}

export function writeMailAttachmentClipboardPayload(
  dataTransfer: DataTransfer,
  payload: MailAttachmentClipboardPayload
) {
  const attachments = uniqueById(payload.attachments);
  const nextPayload: MailAttachmentClipboardPayload = {
    kind: 'streamlain-mail-attachments',
    workspaceId: payload.workspaceId,
    attachments,
  };
  dataTransfer.setData(
    MAIL_ATTACHMENT_CLIPBOARD_TYPE,
    JSON.stringify(nextPayload)
  );
  dataTransfer.setData(
    'text/plain',
    attachments.map(attachment => attachment.filename).join('\n')
  );
}

export function readMailAttachmentClipboardPayload(
  dataTransfer: DataTransfer | null
): MailAttachmentClipboardPayload | null {
  const raw = dataTransfer?.getData(MAIL_ATTACHMENT_CLIPBOARD_TYPE);
  const parsed = parseJsonPayload<unknown>(raw ?? '');
  if (
    !isRecord(parsed) ||
    parsed.kind !== 'streamlain-mail-attachments' ||
    typeof parsed.workspaceId !== 'string' ||
    !Array.isArray(parsed.attachments)
  ) {
    return null;
  }
  const attachments = parsed.attachments.filter(
    isRecord
  ) as MailAttachmentInfo[];
  if (attachments.length === 0) return null;
  return {
    kind: 'streamlain-mail-attachments',
    workspaceId: parsed.workspaceId,
    attachments: uniqueById(attachments),
  };
}

function getMailAttachmentBlobUrl(attachment: MailAttachmentInfo): string {
  return workspaceBlobUrl(attachment.workspaceId, attachment.blobKey);
}

export async function createMailOriginalAttachmentFile(
  attachment: MailAttachmentInfo
): Promise<File> {
  const response = await workspaceApiFetch(
    getMailAttachmentBlobUrl(attachment),
    { credentials: 'include' },
    {
      workspaceId: attachment.workspaceId,
      category: 'workspace-api',
      mode: 'background',
      priority: 'visible',
    }
  );
  if (!response.ok) {
    throw new Error(`Mail attachment copy failed: ${response.status}`);
  }

  const blob = await response.blob();
  return new File([blob], attachment.filename || 'attachment', {
    type: attachment.mimeType || blob.type || 'application/octet-stream',
  });
}

function normalizeMailSenderAddress(value: string | null | undefined) {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const candidate = (/<([^>]+)>/u.exec(trimmed)?.[1] ?? trimmed)
    .trim()
    .toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(candidate) ? candidate : null;
}

function getMailSenderAddress(message: MailMessageInfo | null) {
  if (!message) return null;
  return (
    normalizeMailSenderAddress(message.fromAddress) ??
    normalizeMailSenderAddress(message.fromName)
  );
}

function yieldToMainThread() {
  return new Promise<void>(resolve => window.setTimeout(resolve, 0));
}

function escapeMailHtml(value: string): string {
  return value
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;')
    .replace(/'/gu, '&#39;');
}

function mailTextToHtml(value: string): string | null {
  if (!value.trim()) return null;
  return escapeMailHtml(value).replace(/\r?\n/gu, '<br>');
}

function createMailExportHeaderHtml(
  message: MailMessageInfo,
  body: MailMessageBodyInfo | undefined,
  i18n: I18nInstance
) {
  const subject =
    body?.subject || message.subject || mailText(i18n, 'message.noSubject');
  const sender =
    formatMailSenderDisplayName(message) || message.fromAddress || '';
  const recipients = (message.toAddresses ?? [])
    .map(address => address.trim())
    .filter(Boolean)
    .join(', ');
  const date = formatMailDate(
    message.receivedAt || message.sentAt || message.createdAt
  );
  const size = formatMailBytes(body?.sizeBytes ?? message.sizeBytes);

  return `<section style="box-sizing:border-box;margin:0 0 20px;padding:0 0 16px;border-bottom:1px solid #d0d7de;font:14px/1.45 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1f2328">
    <h1 style="margin:0 0 8px;font-size:22px;line-height:1.25">${escapeMailHtml(subject)}</h1>
    <div style="font-weight:600">${escapeMailHtml(sender)}</div>
    <div style="color:#57606a">${escapeMailHtml(
      mailText(i18n, 'message.toLine', {
        recipients: recipients || mailText(i18n, 'message.noRecipients'),
        size,
      })
    )}</div>
    <div style="color:#57606a">${escapeMailHtml(date)}</div>
  </section>`;
}

function getMailBodyExportHtml(
  message: MailMessageInfo,
  body: MailMessageBodyInfo | undefined,
  sanitizedHtml: SanitizedMailHtml | null,
  i18n: I18nInstance
) {
  if (sanitizedHtml?.html) {
    return sanitizedHtml.html;
  }

  const fallbackText = body?.bodyText || message.snippet || '';
  const fallbackHtml = mailTextToHtml(fallbackText);
  if (fallbackHtml) return fallbackHtml;

  return `<p>${escapeMailHtml(mailText(i18n, 'message.noSubject'))}</p>`;
}

function wrapMailExportHtmlContent(content: string) {
  return `<main data-streamlain-mail-export style="box-sizing:border-box;width:100%;min-height:calc(100vh - ${MAIL_HTML_EXPORT_PADDING_PX * 2}px);padding:${MAIL_HTML_EXPORT_PADDING_PX}px;background-color:${MAIL_HTML_EXPORT_SURFACE_BACKGROUND_COLOR}">
    ${content}
  </main>`;
}

function buildMailExportHtml({
  message,
  body,
  sanitizedHtml,
  i18n,
}: {
  message: MailMessageInfo;
  body: MailMessageBodyInfo | undefined;
  sanitizedHtml: SanitizedMailHtml | null;
  i18n: I18nInstance;
}) {
  const resolvedSanitizedHtml =
    sanitizedHtml ??
    sanitizeMailHtml(body?.bodyHtml, body?.attachments ?? [], {
      remoteImagePlaceholderLabel: mailText(
        i18n,
        'detail.remoteImagePlaceholder'
      ),
    });

  return buildMailHtmlSrcDoc(
    wrapMailExportHtmlContent(`${createMailExportHeaderHtml(message, body, i18n)}
    ${getMailBodyExportHtml(message, body, resolvedSanitizedHtml, i18n)}`),
    {
      backgroundColor: MAIL_HTML_EXPORT_BACKGROUND_COLOR,
      bodyPaddingPx: MAIL_HTML_EXPORT_PADDING_PX,
    }
  );
}

function getMailRemoteImageProxyUrl({
  workspaceId,
  messageId,
  image,
}: {
  workspaceId: string;
  messageId: string;
  image: MailRemoteImage;
}): string {
  return `/api/workspace/${encodeURIComponent(
    workspaceId
  )}/mail/messages/${encodeURIComponent(
    messageId
  )}/remote-images/${encodeURIComponent(image.id)}`;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Mail remote image conversion failed.'));
      }
    });
    reader.addEventListener('error', () => reject(reader.error));
    reader.readAsDataURL(blob);
  });
}

async function fetchMailRemoteImageDataUrl({
  workspaceId,
  messageId,
  image,
}: {
  workspaceId: string;
  messageId: string;
  image: MailRemoteImage;
}): Promise<string> {
  const response = await workspaceApiFetch(
    getMailRemoteImageProxyUrl({ workspaceId, messageId, image }),
    {
      credentials: 'include',
      referrerPolicy: 'no-referrer',
    },
    {
      workspaceId,
      category: 'workspace-api',
      mode: 'background',
      priority: 'visible',
    }
  );
  if (!response.ok) {
    throw new Error(`Mail remote image proxy failed: ${response.status}`);
  }
  return blobToDataUrl(await response.blob());
}

async function resolveMailExportSanitizedHtml({
  workspaceId,
  message,
  body,
  sanitizedHtml,
  includeBlockedRemoteImages,
  i18n,
}: {
  workspaceId: string;
  message: MailMessageInfo;
  body: MailMessageBodyInfo | undefined;
  sanitizedHtml: SanitizedMailHtml | null;
  includeBlockedRemoteImages: boolean;
  i18n: I18nInstance;
}): Promise<SanitizedMailHtml | null> {
  const baseSanitizedHtml =
    sanitizedHtml ??
    sanitizeMailHtml(body?.bodyHtml, body?.attachments ?? [], {
      remoteImagePlaceholderLabel: mailText(
        i18n,
        'detail.remoteImagePlaceholder'
      ),
    });

  if (
    !includeBlockedRemoteImages ||
    !baseSanitizedHtml?.remoteImages.length ||
    !body?.bodyHtml
  ) {
    return baseSanitizedHtml;
  }

  await yieldToMainThread();
  const loadedImages = await Promise.allSettled(
    baseSanitizedHtml.remoteImages.map(async image => {
      const dataUrl = await fetchMailRemoteImageDataUrl({
        workspaceId,
        messageId: message.id,
        image,
      });
      return [image.id, dataUrl] as const;
    })
  );
  const imageDataUrls = new Map(
    loadedImages
      .filter(
        (result): result is PromiseFulfilledResult<readonly [string, string]> =>
          result.status === 'fulfilled'
      )
      .map(result => result.value)
  );
  if (imageDataUrls.size === 0) {
    return baseSanitizedHtml;
  }

  return (
    sanitizeMailHtml(body.bodyHtml, body.attachments ?? [], {
      remoteImagePlaceholderLabel: mailText(
        i18n,
        'detail.remoteImagePlaceholder'
      ),
      resolveRemoteImageUrl: image => imageDataUrls.get(image.id) ?? null,
    }) ?? baseSanitizedHtml
  );
}

async function buildMailExportHtmlForCopy({
  workspaceId,
  message,
  body,
  sanitizedHtml,
  includeBlockedRemoteImages,
  i18n,
}: {
  workspaceId: string;
  message: MailMessageInfo;
  body: MailMessageBodyInfo | undefined;
  sanitizedHtml: SanitizedMailHtml | null;
  includeBlockedRemoteImages: boolean;
  i18n: I18nInstance;
}) {
  const resolvedSanitizedHtml = await resolveMailExportSanitizedHtml({
    workspaceId,
    message,
    body,
    sanitizedHtml,
    includeBlockedRemoteImages,
    i18n,
  });

  return buildMailExportHtml({
    message,
    body,
    sanitizedHtml: resolvedSanitizedHtml,
    i18n,
  });
}

function ensureMailExportSurfaceId(store: Store) {
  if (!store.root) {
    const rootId = store.addBlock('affine:page', { title: new Text('') });
    return store.addBlock('affine:surface' as never, {}, rootId);
  }

  const existingSurface = store.getBlocksByFlavour('affine:surface').at(0);
  if (existingSurface) {
    return existingSurface.id;
  }

  const rootId = store.root?.id;
  if (!rootId) {
    throw new Error('Target document has no root block.');
  }
  return store.addBlock('affine:surface' as never, {}, rootId);
}

function getMailHtmlAttachmentSize() {
  const width = EMBED_CARD_WIDTH[MAIL_HTML_ATTACHMENT_STYLE];
  return {
    width,
    height: Math.round(width * MAIL_HTML_A4_HEIGHT_TO_WIDTH_RATIO),
  };
}

function getMailHtmlAttachmentGroupPlacement(
  store: Store,
  originalAttachmentCount: number
) {
  const htmlSize = getMailHtmlAttachmentSize();
  const originalWidth = EMBED_CARD_WIDTH[MAIL_ORIGINAL_ATTACHMENT_STYLE];
  const originalHeight = EMBED_CARD_HEIGHT[MAIL_ORIGINAL_ATTACHMENT_STYLE];
  const originalStackHeight =
    originalAttachmentCount > 0
      ? originalAttachmentCount * originalHeight +
        (originalAttachmentCount - 1) * MAIL_ORIGINAL_ATTACHMENT_STACK_GAP
      : 0;
  const groupWidth =
    htmlSize.width +
    (originalAttachmentCount > 0
      ? MAIL_ORIGINAL_ATTACHMENT_COLUMN_GAP + originalWidth
      : 0);
  const groupHeight = Math.max(htmlSize.height, originalStackHeight);
  const groupBound = findFreeBlockSnapshotContentBound({
    blocks: (store.root?.children ?? []) as never,
    width: groupWidth,
    height: groupHeight,
    gap: MAIL_HTML_PLACEMENT_GAP,
  });
  const htmlY = groupBound.y + (groupHeight - htmlSize.height) / 2;
  const originalX =
    groupBound.x + htmlSize.width + MAIL_ORIGINAL_ATTACHMENT_COLUMN_GAP;
  const originalStartY =
    groupBound.y + Math.max(0, (groupHeight - originalStackHeight) / 2);

  return {
    htmlXYWH: new Bound(
      groupBound.x,
      htmlY,
      htmlSize.width,
      htmlSize.height
    ).serialize(),
    originalAttachmentXYWHs: Array.from(
      { length: originalAttachmentCount },
      (_, index) =>
        new Bound(
          originalX,
          originalStartY +
            index * (originalHeight + MAIL_ORIGINAL_ATTACHMENT_STACK_GAP),
          originalWidth,
          originalHeight
        ).serialize()
    ),
  };
}

function createMailHtmlExportFilename(
  message: MailMessageInfo,
  body: MailMessageBodyInfo | undefined,
  i18n: I18nInstance
) {
  const subject =
    body?.subject || message.subject || mailText(i18n, 'message.noSubject');
  const safeBase =
    subject
      .replace(/[<>:"/\\|?*\u0000-\u001f]/gu, ' ')
      .replace(/\s+/gu, ' ')
      .trim()
      .replace(/[. ]+$/gu, '')
      .slice(0, 96) || 'mail';
  return `${safeBase}.html`;
}

async function waitForMailHtmlTargetDocServerReady(
  docsService: DocsService,
  docId: string,
  i18n: I18nInstance
) {
  if (!docsService.isLocallyCreated(docId)) return;
  const confirmed = await docsService.waitForServerConfirmed(docId, {
    timeoutMs: 10_000,
    pollMs: 250,
  });
  if (!confirmed) {
    throw new Error(mailText(i18n, 'error.copyHtmlDocNotReady'));
  }
}

async function persistMailHtmlAttachmentBlob({
  docsService,
  isLocalWorkspace,
  workspaceId,
  blobContext,
  docId,
  store,
  file,
  i18n,
}: {
  docsService: DocsService;
  isLocalWorkspace: boolean;
  workspaceId: string;
  blobContext: WorkspaceBlobContext;
  docId: string;
  store: Store;
  file: File;
  i18n: I18nInstance;
}) {
  if (isLocalWorkspace) {
    const localBlobKey = crypto.randomUUID();
    return store.blobSync.set(localBlobKey, file);
  }

  await waitForMailHtmlTargetDocServerReady(docsService, docId, i18n);
  const blob = await uploadWorkspaceBlob(workspaceId, file, {
    docId,
    context: blobContext,
  });
  return blob.blobId;
}

async function createMailHtmlAttachmentFile({
  workspaceId,
  message,
  body,
  sanitizedHtml,
  includeBlockedRemoteImages,
  i18n,
}: {
  workspaceId: string;
  message: MailMessageInfo;
  body: MailMessageBodyInfo | undefined;
  sanitizedHtml: SanitizedMailHtml | null;
  includeBlockedRemoteImages: boolean;
  i18n: I18nInstance;
}) {
  const html = await buildMailExportHtmlForCopy({
    workspaceId,
    message,
    body,
    sanitizedHtml,
    includeBlockedRemoteImages,
    i18n,
  });
  const file = new File(
    [html],
    createMailHtmlExportFilename(message, body, i18n),
    {
      type: 'text/html;charset=utf-8',
    }
  );
  if (file.size > MAIL_HTML_EXPORT_MAX_BYTES) {
    throw new Error(
      mailText(i18n, 'error.copyHtmlTooLarge', {
        maxSize: formatMailBytes(MAIL_HTML_EXPORT_MAX_BYTES),
      })
    );
  }
  return file;
}

export interface MailHtmlImportFileGroup {
  html: File;
  attachments: File[];
}

export async function createMailHtmlImportFileGroups({
  workspaceId,
  sources,
  i18n,
}: {
  workspaceId: string;
  sources: MailHtmlImportDialogSource[];
  i18n: I18nInstance;
}): Promise<MailHtmlImportFileGroup[]> {
  await yieldToMainThread();
  const files: MailHtmlImportFileGroup[] = [];
  for (const source of sources) {
    const html = await createMailHtmlAttachmentFile({
      workspaceId,
      message: source.message,
      body: source.body,
      sanitizedHtml: source.sanitizedHtml,
      includeBlockedRemoteImages: source.includeBlockedRemoteImages,
      i18n,
    });
    const attachments: File[] = [];
    for (const attachment of source.body?.attachments ?? []) {
      attachments.push(await createMailOriginalAttachmentFile(attachment));
    }
    files.push({ html, attachments });
  }
  await yieldToMainThread();
  return files;
}

async function addMailHtmlAttachmentBlockToDoc({
  docsService,
  isLocalWorkspace,
  workspaceId,
  blobContext,
  docId,
  store,
  surfaceId,
  file,
  xywh,
  i18n,
}: {
  docsService: DocsService;
  isLocalWorkspace: boolean;
  workspaceId: string;
  blobContext: WorkspaceBlobContext;
  docId: string;
  store: Store;
  surfaceId: string;
  file: File;
  xywh: string;
  i18n: I18nInstance;
}) {
  const sourceId = await persistMailHtmlAttachmentBlob({
    docsService,
    isLocalWorkspace,
    workspaceId,
    blobContext,
    docId,
    store,
    file,
    i18n,
  });
  return store.addBlock(
    'affine:attachment' as never,
    {
      xywh,
      name: file.name,
      size: file.size,
      type: file.type || 'text/html',
      sourceId,
      embed: true,
      style: MAIL_HTML_ATTACHMENT_STYLE,
    },
    surfaceId
  );
}

async function addMailOriginalAttachmentBlockToDoc({
  docsService,
  isLocalWorkspace,
  workspaceId,
  blobContext,
  docId,
  store,
  surfaceId,
  file,
  xywh,
  i18n,
}: {
  docsService: DocsService;
  isLocalWorkspace: boolean;
  workspaceId: string;
  blobContext: WorkspaceBlobContext;
  docId: string;
  store: Store;
  surfaceId: string;
  file: File;
  xywh: string;
  i18n: I18nInstance;
}) {
  const sourceId = await persistMailHtmlAttachmentBlob({
    docsService,
    isLocalWorkspace,
    workspaceId,
    blobContext,
    docId,
    store,
    file,
    i18n,
  });
  return store.addBlock(
    'affine:attachment' as never,
    {
      xywh,
      name: file.name,
      size: file.size,
      type: file.type || 'application/octet-stream',
      sourceId,
      embed: false,
      style: MAIL_ORIGINAL_ATTACHMENT_STYLE,
    },
    surfaceId
  );
}

function addMailAttachmentRelationConnectorToDoc({
  store,
  surfaceId,
  htmlBlockId,
  attachmentBlockId,
}: {
  store: Store;
  surfaceId: string;
  htmlBlockId: string;
  attachmentBlockId: string;
}) {
  const surface = store.getBlock(surfaceId)?.model as
    | SurfaceBlockModel
    | undefined;
  return surface?.addElement({
    type: 'connector',
    mode: ConnectorMode.Curve,
    source: {
      id: htmlBlockId,
    },
    target: {
      id: attachmentBlockId,
    },
    frontEndpointStyle: PointStyle.None,
    rearEndpointStyle: PointStyle.Arrow,
  });
}

export async function prepareMailHtmlImportSources({
  graphqlService,
  workspaceId,
  messages,
  messageIds,
  trustedSenderAddresses,
  i18n,
}: {
  graphqlService: GraphQLService;
  workspaceId: string;
  messages: readonly MailMessageInfo[];
  messageIds: readonly string[];
  trustedSenderAddresses: ReadonlySet<string>;
  i18n: I18nInstance;
}): Promise<MailHtmlImportDialogSource[]> {
  const messageById = new Map(messages.map(message => [message.id, message]));
  const sources: MailHtmlImportDialogSource[] = [];
  for (const messageId of messageIds) {
    const message = messageById.get(messageId);
    if (!message) continue;
    const response = await graphqlService.gql({
      query: mailMessageBodyQuery,
      variables: {
        workspaceId,
        messageId,
      },
    });
    const body = response.mailMessageBody;
    const sanitizedHtml = sanitizeMailHtml(
      body?.bodyHtml,
      body?.attachments ?? [],
      {
        remoteImagePlaceholderLabel: mailText(
          i18n,
          'detail.remoteImagePlaceholder'
        ),
      }
    );
    const senderAddress = getMailSenderAddress(message);
    const isTrustedSender = Boolean(
      senderAddress && trustedSenderAddresses.has(senderAddress)
    );
    sources.push({
      message,
      body,
      sanitizedHtml,
      senderAddress,
      isTrustedSender,
      includeBlockedRemoteImages: isTrustedSender,
    });
  }
  return sources;
}

export async function copyMailHtmlAttachmentsToDoc({
  docsService,
  guardService,
  isLocalWorkspace,
  workspaceId,
  blobContext,
  targetDocId,
  newDocTitle,
  sources,
  i18n,
}: {
  docsService: DocsService;
  guardService: GuardService;
  isLocalWorkspace: boolean;
  workspaceId: string;
  blobContext: WorkspaceBlobContext;
  targetDocId: string | null;
  newDocTitle: string;
  sources: MailHtmlImportDialogSource[];
  i18n: I18nInstance;
}) {
  if (sources.length === 0) {
    throw new Error(mailText(i18n, 'error.copyHtmlFailed'));
  }
  if (
    targetDocId &&
    !isLocalWorkspace &&
    !docsService.isLocallyCreated(targetDocId)
  ) {
    const canUpdateTarget = await guardService.can('Doc_Update', targetDocId);
    if (!canUpdateTarget) {
      throw new Error(mailText(i18n, 'error.copyHtmlNoWritePermission'));
    }
  }

  const files = await createMailHtmlImportFileGroups({
    workspaceId,
    sources,
    i18n,
  });
  const docId =
    targetDocId ??
    docsService.createDoc({
      primaryMode: 'edgeless',
      title: newDocTitle,
      createInWorkspaceRoot: true,
    }).id;
  if (!targetDocId) {
    await docsService.changeDocTitle(docId, newDocTitle);
  }

  const { doc, release } = docsService.open(docId);
  const disposePriorityLoad = doc.addPriorityLoad(10);
  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    const surfaceId = ensureMailExportSurfaceId(store);
    const attachmentBlockIds: string[] = [];
    for (const fileGroup of files) {
      const placement = getMailHtmlAttachmentGroupPlacement(
        store,
        fileGroup.attachments.length
      );
      const htmlBlockId = await addMailHtmlAttachmentBlockToDoc({
        docsService,
        isLocalWorkspace,
        workspaceId,
        blobContext,
        docId,
        store,
        surfaceId,
        file: fileGroup.html,
        xywh: placement.htmlXYWH,
        i18n,
      });
      attachmentBlockIds.push(htmlBlockId);
      for (const [index, file] of fileGroup.attachments.entries()) {
        const attachmentBlockId = await addMailOriginalAttachmentBlockToDoc({
          docsService,
          isLocalWorkspace,
          workspaceId,
          blobContext,
          docId,
          store,
          surfaceId,
          file,
          xywh: placement.originalAttachmentXYWHs[index]!,
          i18n,
        });
        attachmentBlockIds.push(attachmentBlockId);
        addMailAttachmentRelationConnectorToDoc({
          store,
          surfaceId,
          htmlBlockId,
          attachmentBlockId,
        });
      }
    }
    if (isFileIndexEligibleDocId(workspaceId, docId)) {
      await reconcileFileIndexDb({ workspaceId, docId, store });
    }
    return { docId, attachmentBlockIds };
  } finally {
    disposePriorityLoad();
    release();
  }
}
